/**
 * Browse all colours — base-colour groups as tabs.
 *
 * The server sends the groups stacked, each behind its own heading, which is a
 * complete and readable page on its own. This file upgrades that stack into a
 * tablist. Doing it in that direction rather than the other means a blocked or
 * failed script leaves working markup rather than a set of panels nothing can
 * open — the same bargain `section-product-tabs.js` makes, for the same reason.
 *
 * The tab bar is built here rather than rendered by Liquid, so a shopper without
 * JavaScript never sees a row of buttons that does nothing.
 *
 * Unlike the product tabs there is no narrow-screen fallback to an accordion.
 * Those tabs are a handful of long prose panels, where wrapping the bar costs
 * little; this is thirteen short colour names in front of sixty-plus cards,
 * where collapsing to one family is worth more on a phone than anywhere else.
 * The bar scrolls sideways instead.
 *
 * <colour-group-tabs> re-initialises for free after a theme-editor reload:
 * replaced markup upgrades into a new element instance, and every listener lives
 * on the element or on nodes this file created.
 */
(function () {
  class ColourGroupTabs extends HTMLElement {
    connectedCallback() {
      // Moving an element in the DOM disconnects and reconnects it. When the
      // markup is already upgraded only the dropped listeners go back on;
      // building the bar again would double every tab.
      if (this.initialised) {
        this.listen();
        return;
      }

      this.groups = Array.from(this.querySelectorAll('[data-colour-group]')).filter(
        (group) => group.closest('colour-group-tabs') === this
      );
      // One group is not a set of tabs. Leave the stack alone.
      if (this.groups.length < 2) return;

      this.bar = this.querySelector('[data-colour-tabs-bar]');
      if (!this.bar) return;

      this.initialised = true;
      this.showAll = this.dataset.showAll === 'true';
      this.buttons = [];
      this.active = 0;

      this.onBarClick = this.onBarClick.bind(this);
      this.onBarKeydown = this.onBarKeydown.bind(this);

      this.build();
      this.listen();
      this.select(0, { focus: false });
    }

    disconnectedCallback() {
      this.bar?.removeEventListener('click', this.onBarClick);
      this.bar?.removeEventListener('keydown', this.onBarKeydown);
      this.previous?.removeEventListener('click', this.onArrowClick);
      this.next?.removeEventListener('click', this.onArrowClick);
      this.list?.removeEventListener('scroll', this.onListScroll);
      this.resizeObserver?.disconnect();
      if (this.edgeFrame) cancelAnimationFrame(this.edgeFrame);
    }

    /* ── Carousel ──────────────────────────────────────────────────────── */

    scheduleEdgeUpdate() {
      if (this.edgeFrame) return;
      this.edgeFrame = requestAnimationFrame(() => {
        this.edgeFrame = null;
        this.updateEdges();
      });
    }

    updateEdges() {
      if (!this.previous) return;
      const tolerance = 2;
      const overflows = this.list.scrollWidth > this.list.clientWidth + tolerance;

      // Hidden rather than shown-but-inert when there is nothing to scroll: two
      // permanently dim circles beside a short bar are noise.
      this.previous.hidden = !overflows;
      this.next.hidden = !overflows;
      if (!overflows) return;

      // `Math.abs` because a right-to-left container scrolls negative.
      const offset = Math.abs(this.list.scrollLeft);
      const max = this.list.scrollWidth - this.list.clientWidth;
      this.previous.setAttribute('aria-disabled', String(offset <= tolerance));
      this.next.setAttribute('aria-disabled', String(offset >= max - tolerance));
    }

    onArrowClick(event) {
      const button = event.currentTarget;
      // Clamped here rather than by the native `disabled` attribute — see the
      // stylesheet for why that attribute is not used.
      if (button.getAttribute('aria-disabled') === 'true') return;

      const rtl = getComputedStyle(this).direction === 'rtl';
      const direction = button.dataset.tabArrow === 'previous' ? -1 : 1;
      const styles = getComputedStyle(this.list);
      const gap = parseFloat(styles.columnGap) || 0;
      // A page at a time, less one gap, so the tab at the edge stays partly in
      // view and the jump reads as continuous.
      const page = Math.max(1, this.list.clientWidth - gap);

      this.list.scrollBy({
        left: page * direction * (rtl ? -1 : 1),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    listen() {
      this.bar.addEventListener('click', this.onBarClick);
      this.bar.addEventListener('keydown', this.onBarKeydown);
      if (this.previous) {
        this.previous.addEventListener('click', this.onArrowClick);
        this.next.addEventListener('click', this.onArrowClick);
        this.list.addEventListener('scroll', this.onListScroll, { passive: true });
      }
    }

    /* ── Build ─────────────────────────────────────────────────────────── */

    build() {
      const list = document.createElement('div');
      list.className = 'bac__tablist';
      list.setAttribute('role', 'tablist');
      list.setAttribute('aria-label', this.dataset.barLabel || '');

      const entries = [];
      if (this.showAll) {
        entries.push({
          label: this.dataset.allLabel || 'All',
          count: this.groups.reduce((sum, g) => sum + this.cardCount(g), 0),
          group: null
        });
      }

      this.groups.forEach((group) => {
        entries.push({
          label: group.dataset.colourGroupLabel || '',
          count: this.cardCount(group),
          group
        });
      });

      entries.forEach((entry, index) => {
        const id = `${this.id || 'bac'}-tab-${index}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.id = id;
        button.className = 'bac__tab';
        button.setAttribute('role', 'tab');
        // Roving tabindex: one stop for the whole bar, arrows move within it.
        // A bar of fourteen focusable buttons in front of the content would
        // otherwise be fourteen tab stops to get past.
        button.tabIndex = -1;
        button.setAttribute('aria-selected', 'false');

        const label = document.createElement('span');
        label.className = 'bac__tab-label';
        label.textContent = entry.label;
        button.appendChild(label);

        const count = document.createElement('span');
        count.className = 'bac__tab-count';
        count.textContent = String(entry.count);
        // Decorative: the panel it opens states the same number, and the tab's
        // accessible name should be the colour, not "Blue 5".
        count.setAttribute('aria-hidden', 'true');
        button.appendChild(count);

        this.buttons.push({ button, group: entry.group });
        list.appendChild(button);
      });

      // The headings the bar was built from are now duplicated by the tabs, so
      // they stop being headings. Kept in the DOM as the panel's accessible
      // name rather than deleted, which is what `aria-labelledby` needs.
      this.groups.forEach((group, index) => {
        const heading = group.querySelector('[data-colour-group-heading]');
        const panelId = `${this.id || 'bac'}-panel-${index}`;
        group.id = panelId;
        group.setAttribute('role', 'tabpanel');
        group.tabIndex = 0;
        if (heading) heading.classList.add('visually-hidden');
      });

      this.list = list;

      if (this.dataset.showArrows === 'true') {
        this.previous = this.buildArrow('previous');
        this.next = this.buildArrow('next');
        this.bar.appendChild(this.previous);
        this.bar.appendChild(list);
        this.bar.appendChild(this.next);
      } else {
        this.bar.appendChild(list);
      }

      this.bar.hidden = false;
      this.classList.add('bac--tabbed');

      if (this.previous) {
        this.onArrowClick = this.onArrowClick.bind(this);
        this.onListScroll = this.scheduleEdgeUpdate.bind(this);
        this.updateEdges();

        // The bar's width decides whether the arrows are needed at all, and a
        // font or a theme-editor resize can change it after first paint.
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.scheduleEdgeUpdate());
          this.resizeObserver.observe(list);
        }
      }
    }

    buildArrow(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bac__tab-arrow';
      button.dataset.tabArrow = direction;
      button.setAttribute(
        'aria-label',
        (direction === 'previous' ? this.dataset.previousLabel : this.dataset.nextLabel) || ''
      );
      // Not `aria-controls` to the tablist: the arrows scroll it, they do not
      // own it, and pointing at it made screen readers announce the whole bar.
      button.innerHTML =
        '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="' +
        (direction === 'previous' ? 'm12.5 4.5-5 5.5 5 5.5' : 'm7.5 4.5 5 5.5-5 5.5') +
        '" fill="none" stroke="currentColor" stroke-width="1.5" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      return button;
    }

    cardCount(group) {
      return group.querySelectorAll('.bac__card').length;
    }

    /* ── Selection ─────────────────────────────────────────────────────── */

    select(index, { focus = true } = {}) {
      const entry = this.buttons[index];
      if (!entry) return;
      this.active = index;

      this.buttons.forEach((item, i) => {
        const selected = i === index;
        item.button.setAttribute('aria-selected', String(selected));
        item.button.tabIndex = selected ? 0 : -1;
      });

      // The "All" tab has no group of its own, so it labels itself against every
      // panel; a single-group tab points at just its own.
      const shownGroups = entry.group ? [entry.group] : this.groups;
      this.groups.forEach((group) => {
        const shown = shownGroups.includes(group);
        group.hidden = !shown;
        if (shown) group.setAttribute('aria-labelledby', entry.button.id);
      });

      if (focus) entry.button.focus();
      this.scheduleEdgeUpdate();
    }

    /* ── Events ────────────────────────────────────────────────────────── */

    onBarClick(event) {
      const button = event.target.closest('[role="tab"]');
      if (!button) return;
      const index = this.buttons.findIndex((item) => item.button === button);
      if (index >= 0) this.select(index, { focus: false });
    }

    onBarKeydown(event) {
      const horizontal = { ArrowRight: 1, ArrowLeft: -1 };
      const rtl = getComputedStyle(this).direction === 'rtl';
      let next = null;

      if (event.key in horizontal) {
        const step = horizontal[event.key] * (rtl ? -1 : 1);
        next = (this.active + step + this.buttons.length) % this.buttons.length;
      } else if (event.key === 'Home') {
        next = 0;
      } else if (event.key === 'End') {
        next = this.buttons.length - 1;
      }

      if (next === null) return;
      event.preventDefault();
      this.select(next);
      // Keyboard selection can land on a tab scrolled out of the bar.
      this.buttons[next].button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  if (!customElements.get('colour-group-tabs')) {
    customElements.define('colour-group-tabs', ColourGroupTabs);
  }
})();

/**
 * Colour accordion.
 *
 * Opening a colour drops a full-width row in directly beneath the row that
 * colour sits in, leaving the card where it was. CSS cannot do this: a grid has
 * no way to insert a row after an arbitrary item, and making the item itself
 * span every column pulls it onto a row of its own and shuffles everything after
 * it. So the panel moves in the DOM instead — grid auto-placement follows DOM
 * order, so a full-width item inserted after the last card of a row lands on the
 * row below it.
 *
 * The server sends `details` elements that open in place, which is what a
 * shopper without JavaScript gets. This file takes the toggling over and
 * relocates the panel; the stylesheet keeps the in-place behaviour scoped to the
 * grid this element has not upgraded.
 *
 * Only one panel is open per grid, and there is one row element per grid that the
 * open panel is moved into. Nothing is cloned, so a panel's lazy images stay
 * exactly as unloaded as they were.
 */
(function () {
  /**
   * One request per card, cached for the life of the page.
   *
   * The panels used to arrive printed. That cost 122 KB of section HTML and 1436
   * elements for markup nobody read until they clicked, and it grew with the
   * catalogue. Now the panel ships the links — which double as the manifest,
   * since each already addresses the product and the variant that names the
   * colour — and the card itself is fetched from
   * `templates/product.colour-row.liquid`.
   *
   * `?variant=` is the carrier because it is the only one Liquid reads natively;
   * a query parameter of our own comes back empty. Rendering the card in Liquid
   * rather than from JSON is what keeps one card implementation in the theme
   * instead of two.
   *
   * Same shape as the kit quick view's cache, which is where this is proven.
   */
  const cardCache = new Map();

  function fetchCard(url) {
    if (cardCache.has(url)) return cardCache.get(url);

    const request = fetch(url, {
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const payload = template.content.querySelector('[data-bac-rows-payload]');
        if (!payload) throw new Error('Invalid colour row response');
        return payload.innerHTML;
      })
      .catch((error) => {
        cardCache.delete(url);
        throw error;
      });

    cardCache.set(url, request);
    return request;
  }

  class ColourAccordion extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        this.listen();
        return;
      }

      this.grid = this.querySelector('[data-colour-grid]');
      if (!this.grid) return;

      this.entries = Array.from(this.grid.querySelectorAll('.bac__item'))
        .map((item) => {
          const details = item.querySelector('.bac__details');
          const summary = details && details.querySelector('summary');
          const panel = details && details.querySelector('.bac__panel');
          return details && summary && panel ? { item, details, summary, panel } : null;
        })
        .filter(Boolean);
      if (!this.entries.length) return;

      this.initialised = true;
      this.open = null;

      // The row the open panel is moved into. One per grid, reused, and parked at
      // the end while closed so it never occupies a cell.
      this.row = document.createElement('li');
      this.row.className = 'bac__row';
      // A list item that is not one of the colours. `presentation` keeps it out
      // of the list's count so a screen reader still hears thirteen colours, not
      // fourteen, while everything inside it stays reachable.
      this.row.setAttribute('role', 'presentation');
      this.row.hidden = true;
      this.grid.appendChild(this.row);

      this.entries.forEach(({ details, summary }, index) => {
        // `name` gave the server-rendered version mutually exclusive panels. This
        // file enforces that itself, and leaving the attribute on would let the
        // browser close a panel behind its back.
        details.removeAttribute('name');
        details.open = false;
        summary.setAttribute('aria-expanded', 'false');
        summary.id = summary.id || `${this.id || 'bacacc'}-summary-${index}`;
      });

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.reposition.bind(this);
      this.onPrefetch = this.onPrefetch.bind(this);

      this.listen();
      this.classList.add('bac__accordion--upgraded');
    }

    disconnectedCallback() {
      this.grid?.removeEventListener('click', this.onClick);
      this.grid?.removeEventListener('keydown', this.onKeydown);
      this.grid?.removeEventListener('pointerover', this.onPrefetch);
      this.grid?.removeEventListener('focusin', this.onPrefetch);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    listen() {
      this.grid.addEventListener('click', this.onClick);
      this.grid.addEventListener('keydown', this.onKeydown);
      // A pointer resting on a swatch, or a keyboard reaching it, is enough
      // notice to start the fetch — so the panel is usually already filled by
      // the time it opens. Passive because neither handler touches the event.
      this.grid.addEventListener('pointerover', this.onPrefetch, { passive: true });
      this.grid.addEventListener('focusin', this.onPrefetch, { passive: true });
      // Column count changes with width, so the row the panel belongs under
      // changes too. Both signals, not one or the other: the observer catches a
      // grid that reflows without the window moving — a tab switch, a font
      // arriving — and the window event is the cheap backstop for the observer
      // being throttled or, as in the environment this was verified in, inert.
      // `reposition` is idempotent, so hearing twice costs nothing.
      if ('ResizeObserver' in window && !this.resizeObserver) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.grid);
      }
      window.addEventListener('resize', this.onResize);
    }

    entryFor(node) {
      return this.entries.find((entry) => entry.summary.contains(node)) || null;
    }

    onClick(event) {
      const entry = this.entryFor(event.target);
      if (!entry) return;
      // The native toggle would open the panel where it still sits in the grid,
      // which is the behaviour being replaced.
      event.preventDefault();
      this.toggle(entry);
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;
      const summary = this.open.summary;
      this.close();
      summary.focus();
    }

    toggle(entry) {
      if (this.open === entry) {
        this.close();
        return;
      }
      this.close();

      this.row.appendChild(entry.panel);
      this.row.setAttribute('aria-labelledby', entry.summary.id);
      this.row.hidden = false;
      entry.summary.setAttribute('aria-expanded', 'true');
      entry.item.classList.add('bac__item--open');
      this.open = entry;
      this.reposition();
      this.hydrate(entry);
    }

    onPrefetch(event) {
      const entry = this.entryFor(event.target);
      if (entry) this.cardUrls(entry.panel).forEach((url) => fetchCard(url).catch(() => {}));
    }

    /** The panel's links are the manifest; the view turns one into its card. */
    cardUrls(panel) {
      const links = panel.querySelector('[data-bac-links]');
      if (!links) return [];

      return Array.from(links.querySelectorAll('a[href]')).map((anchor) => {
        const url = new URL(anchor.getAttribute('href'), window.location.origin);
        url.searchParams.set('view', 'colour-row');
        return url.pathname + url.search;
      });
    }

    async hydrate(entry) {
      const panel = entry.panel;
      if (panel.dataset.bacLoaded === 'true' || panel.dataset.bacLoading === 'true') return;

      const urls = this.cardUrls(panel);
      if (!urls.length) return;

      panel.dataset.bacLoading = 'true';
      try {
        const cards = await Promise.all(urls.map((url) => fetchCard(url)));

        const list = document.createElement('ul');
        list.className = 'bac__products';
        list.setAttribute('role', 'list');
        list.innerHTML = cards.join('');

        const links = panel.querySelector('[data-bac-links]');
        if (links) {
          links.replaceWith(list);
        } else {
          panel.appendChild(list);
        }
        panel.dataset.bacLoaded = 'true';
        // The cards are taller than the links they replaced; the row itself does
        // not move, but asking again costs nothing and covers a grid that
        // reflowed while the request was in flight.
        this.reposition();
      } catch (error) {
        // Deliberately silent, and the links stay exactly where they were: a
        // shopper can still reach the product in this colour, which is the whole
        // point of shipping links rather than an empty box.
      } finally {
        delete panel.dataset.bacLoading;
      }
    }

    close() {
      if (!this.open) return;
      // Back into its own `details`, so the markup returns to the shape the
      // server sent and a second open finds the panel where it expects it.
      this.open.details.appendChild(this.open.panel);
      this.open.summary.setAttribute('aria-expanded', 'false');
      this.open.item.classList.remove('bac__item--open');
      this.row.hidden = true;
      this.row.removeAttribute('aria-labelledby');
      this.open = null;
    }

    /* Place the row immediately after the last card sharing the open card's row.
       Items in one grid row share an offsetTop, which holds however many columns
       there are and whatever the gaps do. */
    reposition() {
      if (!this.open) return;
      const items = this.entries.map((entry) => entry.item);
      const top = this.open.item.offsetTop;
      let last = this.open.item;
      items.forEach((item) => {
        if (Math.abs(item.offsetTop - top) < 2) last = item;
      });
      if (last.nextElementSibling !== this.row) {
        last.after(this.row);
      }
    }
  }

  if (!customElements.get('colour-accordion')) {
    customElements.define('colour-accordion', ColourAccordion);
  }
})();
